import axios from "axios";
import { API_URL } from "const/constants";

const BASE = `${API_URL}/referral-partners`;

const TONES = {
  ACTIVE: "success",
  INVITED: "info",
  ONBOARDING: "warning",
  PRE_ENROLMENT: "default",
  SUSPENDED: "error",
  ARCHIVED: "default",
  CANCELLED: "default",
};

export const partnerStatusTone = (status) => TONES[status] || "default";

// A pre-enrolment partner is a colleague whose earlier referrals were migrated before they joined.
const LABELS = { PRE_ENROLMENT: "Not enrolled yet" };

export const partnerStatusLabel = (status) => LABELS[status] || status;

const shortDate = (value) =>
  new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

/**
 * One line under the status chip for a partner whose invitation is still outstanding: when it
 * went out and how often, or that the link has lapsed. Null when there is nothing to resend.
 */
export const invitationCaption = (invitation) => {
  if (!invitation) return null;
  if (invitation.status === "EXPIRED") return "Link expired";
  const sentOn = invitation.lastSentAt || invitation.sentAt;
  if (!sentOn) return null;
  const resends = Math.max(0, (invitation.sentCount || 1) - 1);
  const suffix = resends > 0 ? ` · resent ${resends}×` : "";
  return `Sent ${shortDate(sentOn)}${suffix}`;
};

export const buildPartnerListParams = ({ page, size, search, status }) => {
  const params = { page, size };
  const trimmedSearch = String(search || "").trim();
  if (trimmedSearch) params.search = trimmedSearch;
  if (status) params.status = status;
  return params;
};

export const listPartners = async (params) => {
  const { data } = await axios.get(BASE, { params: buildPartnerListParams(params) });
  return data?.data ?? data;
};

export const getPartner = async (id) => {
  const { data } = await axios.get(`${BASE}/${id}`);
  return data?.data ?? data;
};

export const updatePartner = async (id, payload) => {
  const { data } = await axios.put(`${BASE}/${id}`, payload);
  return data?.data ?? data;
};

/** Payout-gated: the profile everywhere else carries only the last four digits. */
export const getPartnerBankAccount = async (id) => {
  const { data } = await axios.get(`${BASE}/${id}/bank-account`);
  return data?.data ?? data;
};

export const suspendPartner = (id, reason) => axios.post(`${BASE}/${id}/suspend`, { reason });
export const reactivatePartner = (id) => axios.post(`${BASE}/${id}/reactivate`);
export const verifyPartnerDocument = (partnerId, documentId, approve, reason) =>
  axios.post(`${BASE}/${partnerId}/documents/${documentId}/verify`, { approve, reason });
export const resendInvitation = (id) => axios.post(`${BASE}/invitations/${id}/resend`);
export const revokeInvitation = (id) => axios.delete(`${BASE}/invitations/${id}`);

export const listCommissions = async (partnerId, params) => {
  const { data } = await axios.get(`${BASE}/${partnerId}/commissions`, { params });
  return data?.data ?? data;
};

export const approveCommissions = async (commissionIds) => {
  const { data } = await axios.post(`${BASE}/commissions/approve`, { commissionIds });
  return data?.data ?? data;
};

export const createPayout = async (partnerId, payload) => {
  const { data } = await axios.post(`${BASE}/${partnerId}/payouts`, payload);
  return data?.data ?? data;
};

export const reversePayout = async (partnerId, payoutId, reason) => {
  const { data } = await axios.post(`${BASE}/${partnerId}/payouts/${payoutId}/reverse`, { reason });
  return data?.data ?? data;
};

export const attachPayoutReceipts = async (partnerId, payoutId, receiptDocuments) => {
  const { data } = await axios.post(`${BASE}/${partnerId}/payouts/${payoutId}/receipts`, {
    receiptDocuments,
  });
  return data?.data ?? data;
};

export const listPayouts = async (partnerId, params) => {
  const { data } = await axios.get(`${BASE}/${partnerId}/payouts`, { params });
  return data?.data ?? data;
};

export const listPartnerMessages = async (partnerId, params) => {
  const { data } = await axios.get(`${BASE}/${partnerId}/messages`, { params });
  return data?.data ?? data;
};

/** The partner's conversations: the general thread plus one per client they have talked about. */
export const listPartnerMessageThreads = async (partnerId) => {
  const { data } = await axios.get(`${BASE}/${partnerId}/message-threads`, {
    params: { page: 0, size: 100 },
  });
  return data?.data ?? data;
};

export const sendPartnerMessage = async (partnerId, payload) => {
  const { data } = await axios.post(`${BASE}/${partnerId}/messages`, payload);
  return data?.data ?? data;
};

export const markPartnerMessagesRead = async (messageIds) => {
  const { data } = await axios.post(`${BASE}/messages/mark-read`, { messageIds });
  return data?.data ?? data;
};
